/** @format */

import React,{useState,useContext,useEffect} from 'react'
import {useNavigate,useParams} from 'react-router-dom'
import ButtonPrimaryWithIcon from '../Buttons/ButtonPrimaryWithIcon'
import MenuLink from '../Links/MenuLink'
import icon from '../../assets/icon_light.svg'
import {Link} from 'react-router-dom'
import Subheading from '../Text/Subheading'
import UserContext from '../../contexts/UserContext'
import {getDoc,doc} from 'firebase/firestore'
import {db,rt} from '../../helpers/firebase'
import InputHeading from '../Inputs/InputHeading'
import useDidMountEffect from '../../hooks/useDidMountEffect'
import {getDownloadURL,ref} from 'firebase/storage'
import {storage} from '../../helpers/firebase'
import useUserSubscription from '../../hooks/useUserSubscription'
import {getPlanLimits} from '../../config/planLimits'
import CreditsContext from '../../contexts/CreditsContext'
import ButtonSecondary from '../Buttons/ButtonSecondary'
import Icon from '../Text/Icon'
import ProjectsContext from '../../contexts/ProjectsContext'
import {child,get,onValue,push,remove,ref as rtRef,update} from 'firebase/database'
import {encodeEmailForRTDB} from '../../helpers/emailEncoding'
import useNotifications from '../../hooks/useNotifications'
import NotificationPanel from './NotificationPanel'
import ButtonPrimary from '../Buttons/ButtonPrimary'
import Card from '../Cards/Card'
import Input from '../Inputs/Input'
import Text from '../Text/Text'
import useValidator from '../../hooks/useValidator'
import ErrorContext from '../../contexts/ErrorContext'
import {toPng} from 'html-to-image'
import {useReactFlow,getRectOfNodes,getTransformForBounds} from 'reactflow'
import {logEvent} from 'firebase/analytics'
import analytics from '../../helpers/analytics'


function downloadImage(dataUrl,filename='flowchart') {
  const a=document.createElement('a');

  a.setAttribute('download',`${filename}.png`);
  a.setAttribute('href',dataUrl);
  a.click();
}

function Navbar({onVersionHistory,...props},ref) {
  const {projectId,uid}=useParams()
  const navigate=useNavigate()
  const [projectTitle,setProjectTitle]=useState('')
  const [user]=useContext(UserContext)
  const [linkCopied,setLinkCopied]=useState(false)
  const [embedCopied,setEmbedCopied]=useState(false)
  const [embedLinkCopied,setEmbedLinkCopied]=useState(false)
  const {getNodes}=useReactFlow();
  const [userPlan,setUserPlan]=useState('free')
  const {getUserPlan}=useUserSubscription()
  const [collaboratorEmail,setCollaboratorEmail]=useState('')
  const [collaboratorRole,setCollaboratorRole]=useState('editor')
  const [collaborators,setCollaborators]=useState([]) // [{email, role}]
  const [showInviteCollaborator,setShowInviteCollaborator]=useState(false)
  const [showShare,setShowShare]=useState(false)
  const [collaboratorEmailError,setCollaboratorEmailError]=useState(false)
  const [showNotifications,setShowNotifications]=useState(false)
  const [credits,updateCredits]=useContext(CreditsContext)
  const {notifications,unreadCount,markRead,markAllRead,deleteNotif}=useNotifications(user?.uid)
  const [projects,updateProjects]=useState(ProjectsContext)
  const [photoUrl,setPhotoUrl]=useState('https://firebasestorage.googleapis.com/v0/b/flowsage-web.appspot.com/o/default-user.jpg?alt=media&token=e6566b13-e279-4a9f-9171-6232dafaa174')
  const validator=useValidator()
  const setError=useContext(ErrorContext)

  useEffect(() => {
    getUserPlan().then((plan) => {
      setUserPlan(plan)
    })
    updateCredits()
  },[user?.uid,updateCredits,getUserPlan])

  useEffect(() => {
    if(linkCopied) {
      setTimeout(() => {
        setLinkCopied(false)
      },1500)
    }
  },[linkCopied])

  useEffect(() => {
    if(embedCopied) {
      setTimeout(() => {
        setEmbedCopied(false)
      },1500)
    }
  },[embedCopied])

  useEffect(() => {
    if(embedLinkCopied) {
      setTimeout(() => {
        setEmbedLinkCopied(false)
      },1500)
    }
  },[embedLinkCopied])

  useEffect(() => {
    if(projectId&&!uid) {
      const projectRef=rtRef(rt,'projects/'+user.uid+'/'+projectId)
      get(projectRef).then(snapshot => {
        if(snapshot.exists()) {
          const project=snapshot.val()
          setProjectTitle(project.title)
        }
      })
      onValue(child(projectRef,'collaborators'),snapshot => {
        const data=snapshot.val()||{}
        setCollaborators(Object.entries(data).map(([encoded,val]) => ({
          email: decodeURIComponent(encoded),
          role: val?.role||'editor'
        })))
      })
    }
  },[projectId,user?.uid,uid])

  function handleChangeTitle() {
    if(projectId) {
      if(projectTitle.length>50) {
        setError('Title must be less than 50 characters')
        return
      }
      const projectRef=rtRef(rt,'projects/'+user.uid+'/'+projectId)
      update(projectRef,{title: projectTitle.trim()||'Untitled Flowchart'})
    }
  }

  function makeProjectPublic() {
    const projectRef=rtRef(rt,'projects/'+user.uid+'/'+projectId)
    update(projectRef,{public: true})
  }

  function addCollaborator(e) {
    e.preventDefault()
    if(validator.email(collaboratorEmail)[0]) {
      if(collaborators.some(c => c.email===collaboratorEmail)) {
        setCollaboratorEmailError(true)
        document.addEventListener('keyup',() => setCollaboratorEmailError(false),{once: true})
        return
      }

      const planLimits=getPlanLimits(userPlan)
      if(planLimits.collaboratorsPerProject!==Infinity&&collaborators.length>=planLimits.collaboratorsPerProject) {
        setCollaboratorEmailError(true)
        setError('You have reached the maximum number of collaborators for your plan')
        return
      }

      const projectRef=rtRef(rt,'projects/'+user.uid+'/'+projectId)
      update(child(projectRef,'collaborators/'+encodeEmailForRTDB(collaboratorEmail)),{role: collaboratorRole})
      // Send in-app notification to invitee's collaboratorsMap entry
      // Note: we don't know their UID so we use the collaboratorsMap reverse-lookup
      // Store pending invite notification under their encoded email for them to pick up on login
      push(rtRef(rt,`pendingInvites/${encodeEmailForRTDB(collaboratorEmail)}`),{
        fromName: user.displayName||user.email,
        projectId,
        role: collaboratorRole,
        createdAt: Date.now()
      })
      setCollaboratorEmail('')
      setCollaboratorRole('editor')
    } else {
      setCollaboratorEmailError(true)
      document.addEventListener('keyup',() => setCollaboratorEmailError(false),{once: true})
    }
  }

  function changeCollaboratorRole(collaboratorEmail,newRole) {
    const projectRef=rtRef(rt,'projects/'+user.uid+'/'+projectId)
    update(child(projectRef,'collaborators/'+encodeEmailForRTDB(collaboratorEmail)),{role: newRole})
  }

  function removeCollaborator(collaboratorEmail) {
    const projectRef=rtRef(rt,'projects/'+user.uid+'/'+projectId)
    remove(child(child(projectRef,'collaborators'),encodeEmailForRTDB(collaboratorEmail)))
  }

  function exportFlowchart() {
    const nodesBounds=getRectOfNodes(getNodes());
    let transform=getTransformForBounds(nodesBounds,1000,1000,0.5,2);
    logEvent(analytics,'export_flowchart')

    toPng(document.querySelector('.react-flow__viewport'),{
      backgroundColor: 'rgba(0,0,0,0)',
      width: 1000,
      height: 1000,
      style: {
        width: 1000,
        height: 1000,
        transform: `translate(${transform[0]}px, ${transform[1]}px) scale(${transform[2]})`,
      },
    }).then((data) => downloadImage(data,projectTitle.toLowerCase().replace(/ /g,'_')))
  }

  return (
    <nav className="box-border flex justify-between items-center p-4 w-full border-b" ref={ref} {...props}>
      <div className="hidden justify-start items-center my-auto w-full md:flex">
        <Link className='flex-1 max-w-3tiny' to='/dashboard'>
          <img src={icon} alt="logo" className="p-0 max-w-3tiny" />
        </Link>
        {(projectId&&!uid)&&<form onSubmit={(e) => {e.preventDefault(); handleChangeTitle(); e.target.firstChild.blur()}}><InputHeading onBlur={handleChangeTitle} value={projectTitle} setValue={setProjectTitle} /></form>}
      </div>
      <div className="my-auto w-1/5 md:hidden max-w-3tiny">
        <Link to='/dashboard'>
          <img src={icon} alt="logo" />
        </Link>
      </div>
      <div className='flex gap-2 justify-center items-center'>
        {(projectId&&!uid&&onVersionHistory)&&(
          <button
            onClick={onVersionHistory}
            className='relative py-2 rounded-full transition-colors hover:bg-gray-100'
            title='Version History'
            aria-label='Open version history'
          >
            <Icon icon='clock-history' className='text-xl text-gray-600' />
          </button>
        )}
        {/* Notification bell */}
        <div className='relative'>
          <button
            onClick={() => setShowNotifications(v => !v)}
            className='relative py-2 rounded-full transition-colors hover:bg-gray-100'
            title='Notifications'
          >
            <Icon icon='bell' className='text-xl text-gray-600' />
            {unreadCount>0&&(
              <span className='absolute top-0.5 right-0.5 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full'>
                {unreadCount>9? '9+':unreadCount}
              </span>
            )}
          </button>
          {showNotifications&&(
            <NotificationPanel
              notifications={notifications}
              onMarkRead={markRead}
              onMarkAllRead={markAllRead}
              onDelete={deleteNotif}
              onClose={() => setShowNotifications(false)}
            />
          )}
        </div>
        {(projectId&&!uid)&&<div className='relative'><ButtonSecondary onClick={() => setShowShare(showShare => !showShare)} className='hidden w-32 border border-dashed shadow-none text-primary-700 md:block lg:w-32 bg-light hover:bg-primary-50 border-primary lg:shadow-none hover:shadow-none'>Share <Icon className={'inline !px-1'} icon='upload' /></ButtonSecondary>{showShare&&<Card className='absolute !w-72 sm:!w-80 !top-[calc(100%+8px)] h-min right-0 z-50 !border shadow-xl'>
          <Subheading className='px-0 text-center'>Share your project</Subheading>
          <Card onClick={() => {setLinkCopied(true); makeProjectPublic(); navigator.clipboard.writeText('https://www.flowsage.co/dashboard/design/public/'+user?.uid+'/'+projectId)}} clickable className='flex !flex-row lg:!flex-row items-center justify-start gap-2 p-2 mt-4 w-full' >
            <Icon className='text-primary !px-1 lg:!px-1 text-2xl' icon={linkCopied? 'check-lg':'link-45deg'} />
            <Text className='text-primary'>Copy Link</Text>
          </Card>
          <Card onClick={() => {exportFlowchart()}} clickable className='flex !flex-row lg:!flex-row items-center justify-start gap-2 p-2 mt-2 w-full' >
            <Icon className='text-primary !px-1 lg:!px-1 text-2xl' icon='download' />
            <Text className='text-primary'>Export as PNG</Text>
          </Card>
          <Card onClick={() => {makeProjectPublic(); window.location.href=`mailto:?subject=${projectTitle}%20%7C%20Flowsage&body=${'https://www.flowsage.co/dashboard/design/public/'+user?.uid+'/'+projectId}`}} clickable className='flex !flex-row lg:!flex-row items-center justify-start gap-2 p-2 mt-2 w-full' >
            <Icon className='text-primary !px-1 lg:!px-1 text-2xl' icon='envelope' />
            <Text className='text-primary'>Email</Text>
          </Card>
          <div className='flex gap-2 justify-between items-center w-full'>
            <Card onClick={() => {
              setEmbedCopied(true); makeProjectPublic(); navigator.clipboard.writeText(
                `<iframe src="https://www.flowsage.co/embed/${user?.uid}/${projectId}" width="100%" height="100%" style="border: none;"></iframe>`
              )
            }} clickable className='flex !flex-row lg:!flex-row items-center justify-start gap-2 p-2 mt-2 w-full' >
              <Icon className='text-primary !px-1 lg:!px-1 text-2xl' icon={embedCopied? 'check-lg':'code'} />
              <Text className='text-primary'>Copy Embed Code</Text>
            </Card>
            <Card onClick={() => {
              setEmbedLinkCopied(true); makeProjectPublic(); navigator.clipboard.writeText(
                `https://www.flowsage.co/embed/${user?.uid}/${projectId}`
              )
            }} clickable className='flex !flex-row lg:!flex-row items-center justify-start gap-2 p-2 mt-2 w-full' >
              <Icon className='text-primary !px-1 lg:!px-1 text-2xl' icon={embedLinkCopied? 'check-lg':'link-45deg'} />
              <Text className='text-primary'>Copy Embed Link</Text>
            </Card>
          </div>
          <Text className='mt-4 !leading-tight text-left text-zinc-400 text-md lg:text-sm'>Anyone with this link can view the project. If you want to allow editing, add them as a collaborator.</Text>
        </Card>}</div>}
        {(projectId&&!uid)&&<div className='relative'><ButtonPrimary onClick={() => {
          setShowInviteCollaborator(showInviteCollaborator => !showInviteCollaborator)
        }} className='hidden w-32 border border-dashed shadow-none md:block lg:w-32 hover:bg-primary-50 border-primary lg:shadow-none hover:shadow-none'>Invite</ButtonPrimary>
          {showInviteCollaborator&&<Card className='absolute !w-72 sm:!w-80 !top-[calc(100%+8px)] h-min right-0 z-50 !border shadow-xl'>
            <Subheading className='px-0 text-center'>Invite Collaborator</Subheading>
            <form onSubmit={addCollaborator} className='flex flex-col gap-2 w-full'>
              <div className='flex gap-2 items-center w-full'>
                <Input containerClassName='!w-full !flex-1 px-0' value={collaboratorEmail} hasValidationError={collaboratorEmailError} setValue={setCollaboratorEmail} className='lg:!w-full !w-full !flex-1' placeholder='Email' />
                <ButtonPrimary type='submit' className='!w-auto lg:!w-auto px-4 !flex-0'>Invite</ButtonPrimary>
              </div>
              <select value={collaboratorRole} onChange={e => setCollaboratorRole(e.target.value)} className='px-2 py-1.5 w-full text-sm bg-white rounded border border-dark-200 focus:outline-none focus:ring-1 focus:ring-primary'>
                <option value='viewer'>Viewer — read only</option>
                <option value='editor'>Editor — can edit</option>
                <option value='admin'>Admin — can manage collaborators</option>
              </select>
            </form>
            {collaborators.length>0&&<><Subheading className='px-0 pt-6 pb-3 text-center'>Collaborators</Subheading>
              <div className='flex flex-col gap-3 w-full'>
                {collaborators.map(c => (
                  <div key={c.email} className='flex gap-2 items-center'>
                    <Text className='flex-1 text-sm font-semibold truncate' title={c.email}>{c.email}</Text>
                    <select
                      value={c.role}
                      onChange={e => changeCollaboratorRole(c.email,e.target.value)}
                      className='px-1 py-0.5 text-xs bg-white rounded border border-dark-200 focus:outline-none focus:ring-1 focus:ring-primary'
                    >
                      <option value='viewer'>Viewer</option>
                      <option value='editor'>Editor</option>
                      <option value='admin'>Admin</option>
                    </select>
                    <ButtonSecondary onClick={() => removeCollaborator(c.email)} className='!w-auto px-2 py-0.5 text-xs !flex-0'>Remove</ButtonSecondary>
                  </div>
                ))}
              </div></>}
          </Card>}</div>}
        <div className="block relative justify-center align-center">
          <img alt='Profile' onClick={() => navigate('/dashboard/settings')} src={user?.photoURL||'https://firebasestorage.googleapis.com/v0/b/flowsage-web.appspot.com/o/default-user.jpg?alt=media&token=e6566b13-e279-4a9f-9171-6232dafaa174'} className='object-cover p-2 rounded-full cursor-pointer max-w-3tiny aspect-square ms-clarity-hidden' />
          {userPlan==='premium'&&<div className='absolute -right-0 -bottom-0 px-2 py-0.5 text-xs rounded-full border border-primary bg-primary-100 text-primary'>Pro</div>}
          {userPlan==='plus'&&<div className='absolute -right-0 -bottom-0 px-2 py-0.5 text-xs rounded-full border border-primary bg-primary-100 text-primary'>Plus</div>}
        </div>
      </div>
    </nav>
  )
}

export default React.forwardRef(Navbar)